import { ArrowRight, Building2, FileSignature, Users } from "lucide-react";

import { Link } from "react-router-dom";
import ComponentErrorBoundary from "@components/ui-components/error-boundary";
import DashboardHeader from "./dashboard-header";
import StatusCard from "./status-card";

// A self-payout brand pays its publishers itself, so the summary endpoint answers
// with head-count stats only — no custody balance, no debit notes, no payout
// volume, no pending-action feed. The board shows what is actually there rather
// than a grid of zeroes for numbers that will never arrive.
const SelfPayoutDashboard = ({ stats, generatedAt, refreshing, refresh }) => {
  const tiles = [
    {
      label: "Total brands",
      value: stats.total_brands ?? 0,
      hint : "On self payout",
      icon : Building2,
      to   : "/brands"
    },
    {
      label: "Active publishers",
      value: stats.active_publishers ?? 0,
      hint : "Across those brands",
      icon : Users,
      to   : "/publishers"
    }
  ];

  return (
    <div className="space-y-6 p-6">
      <DashboardHeader
        title="Ops control room"
        subtitle="Self-payout brands settle with their publishers directly — Nook Pay tracks the accounts and contracts, not the money."
        generatedAt={generatedAt}
        refreshing={refreshing}
        refresh={refresh} />

      <ComponentErrorBoundary name="self-payout stats">
      <div className="grid gap-3 sm:grid-cols-2 lg:max-w-3xl">
        {tiles.map((tile) => (
          <StatusCard key={tile.label} to={tile.to}>
            <div className="flex items-center justify-between text-xs text-muted">
              <span className="font-medium uppercase tracking-wider">{tile.label}</span>
              <tile.icon className="h-4 w-4" />
            </div>
            <div className="mt-2 text-3xl font-semibold">{tile.value}</div>
            <span className="mt-2 inline-flex items-center gap-1 text-xs text-accent group-hover:underline">
              {tile.hint} <ArrowRight className="h-3 w-3" />
            </span>
          </StatusCard>
        ))}
      </div>
      </ComponentErrorBoundary>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm lg:max-w-3xl">
        <h2 className="font-semibold">No funds move through Nook Pay here</h2>
        <p className="mt-1 text-sm text-muted">
          Debit notes and disbursements only apply to managed-payout brands. Switch the
          payment model in the header to work on those.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Link
            to="/contracts"
            className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50">
            <FileSignature className="h-3.5 w-3.5" />
            Contracts
          </Link>
          <Link
            to="/brands"
            className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50">
            <Building2 className="h-3.5 w-3.5" />
            Brands
          </Link>
          <Link
            to="/publishers"
            className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50">
            <Users className="h-3.5 w-3.5" />
            Publishers
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SelfPayoutDashboard;
